import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  Stack,
  Chip,
  Checkbox,
  FormControlLabel,
  Link,
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import AnalyticsOutlinedIcon from '@mui/icons-material/AnalyticsOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { clearError } from '../store/slices/authSlice';
import { API_ORIGIN } from '../config/api';
import { LogoIcon } from './Logo';
import { PublicSiteChrome, publicPageSx } from './site/PublicSiteChrome';
import Footer from './Footer';

const TOKENS = {
  primary: '#1E40AF',
  secondary: '#3B82F6',
  accent: '#D97706',
  accentGlow: 'rgba(217, 119, 6, 0.35)',
  bgDark: '#0B132B',
  bgSurface: '#0F172A',
  bgCard: 'rgba(15, 23, 42, 0.9)',
  border: 'rgba(219, 234, 254, 0.15)',
  borderActive: 'rgba(59, 130, 246, 0.5)',
  textPrimary: '#F8FAFC',
  textSecondary: '#94A3B8',
  textHighlight: '#60A5FA',
};

const FEATURES = [
  { icon: <LayersOutlinedIcon fontSize="small" />, label: 'Vector & Raster Layers' },
  { icon: <AnalyticsOutlinedIcon fontSize="small" />, label: 'Spatial Query Solvers' },
  { icon: <MapOutlinedIcon fontSize="small" />, label: 'DIGIPIN 4m Grid Engine' },
  { icon: <SecurityOutlinedIcon fontSize="small" />, label: 'Enterprise Google OAuth' },
] as const;

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const { isLoading, error, isAuthenticated } = useAppSelector((state) => state.auth);
  const [localError, setLocalError] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [agreed, setAgreed] = useState(true);
  const isSignUp =
    window.location.pathname === '/signup' || window.location.pathname === '/sign-up';

  const getSafeRedirect = () => {
    const r = searchParams.get('redirect') || '';
    if (r.startsWith('/') && !r.startsWith('//')) return r;
    return '/dashboard';
  };

  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam === 'oauth_cancelled')
      setLocalError('Sign-in cancelled. Please try again when ready.');
    else if (errorParam === 'auth_failed')
      setLocalError('Authentication failed. Please try signing in again.');
    else if (errorParam === 'no_token')
      setLocalError('No session token received from auth provider.');
    else if (errorParam) setLocalError(decodeURIComponent(errorParam));

    const messageParam = searchParams.get('message');
    if (messageParam === 'logged_out') {
      setInfoMessage('You have been signed out successfully.');
    } else if (messageParam && !errorParam) {
      setInfoMessage(decodeURIComponent(messageParam));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when query string changes
  }, [searchParams]);

  useEffect(() => {
    if (isAuthenticated) navigate(getSafeRedirect(), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- redirect path is derived from searchParams
  }, [isAuthenticated, navigate]);

  const handleGoogleAuth = () => {
    if (!agreed) {
      setLocalError('Please agree to the Terms of Service and Privacy Policy to continue.');
      return;
    }
    dispatch(clearError());
    setLocalError(null);
    const redirectUrl = getSafeRedirect();
    window.location.href = `${API_ORIGIN}/api/auth/google?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleClearError = () => {
    dispatch(clearError());
    setLocalError(null);
  };

  const displayError = localError || error;

  return (
    <Box
      sx={{
        ...publicPageSx,
        width: '100%',
        maxWidth: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: TOKENS.bgDark,
        color: TOKENS.textPrimary,
        overflowX: 'hidden',
        overflowY: 'auto',
      }}
    >
      <PublicSiteChrome active="login" subtitle={isSignUp ? 'Sign Up' : 'Sign In'} />

      <Box
        component="main"
        sx={{
          flex: '1 1 auto',
          width: '100%',
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          py: { xs: 3, sm: 4, md: 6 },
        }}
      >
        <Container
          maxWidth="lg"
          sx={{
            width: '100%',
            minWidth: 0,
            px: { xs: 2, sm: 3, md: 4 },
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'minmax(0, 1fr)',
                md: 'minmax(0, 1fr) minmax(0, min(440px, 100%))',
              },
              gap: { xs: 4, sm: 5, md: 6, lg: 8 },
              alignItems: 'center',
              width: '100%',
              minWidth: 0,
            }}
          >
            {/* Left hero column — collapses above the card on small screens */}
            <Box sx={{ minWidth: 0, maxWidth: '100%', order: { xs: 2, md: 1 } }}>
              <Chip
                label="SECURE OAUTH 2.0 AUTHENTICATION"
                size="small"
                sx={{
                  bgcolor: 'rgba(217, 119, 6, 0.15)',
                  color: TOKENS.accent,
                  border: `1px solid ${TOKENS.accentGlow}`,
                  fontWeight: 700,
                  fontSize: 11,
                  mb: 2,
                  maxWidth: '100%',
                }}
              />
              <Typography
                component="h1"
                sx={{
                  fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.75rem', lg: '3.25rem' },
                  fontWeight: 800,
                  letterSpacing: '-0.03em',
                  lineHeight: 1.15,
                  mb: 2,
                  wordBreak: 'break-word',
                }}
              >
                {isSignUp ? 'Start with the Map.' : 'Return to Ground Truth.'}
                <Box
                  component="span"
                  sx={{
                    color: TOKENS.textHighlight,
                    display: 'block',
                    fontWeight: 400,
                    mt: 0.5,
                  }}
                >
                  {isSignUp ? 'Your Spatial Workspace Awaits.' : 'Your Maps & Analytics Ready.'}
                </Box>
              </Typography>

              <Typography
                sx={{
                  color: TOKENS.textSecondary,
                  fontSize: { xs: '0.95rem', sm: '1.05rem' },
                  lineHeight: 1.6,
                  mb: 3,
                  maxWidth: 520,
                }}
              >
                Sign in with your Google account to manage spatial layers, execute DIGIPIN grid
                encodings, and export vector CAD drawings.
              </Typography>

              <Stack direction="row" useFlexGap gap={1} sx={{ maxWidth: '100%', flexWrap: 'wrap' }}>
                {FEATURES.map((feature) => (
                  <Chip
                    key={feature.label}
                    icon={feature.icon}
                    label={feature.label}
                    sx={{
                      bgcolor: 'rgba(15, 23, 42, 0.7)',
                      color: TOKENS.textPrimary,
                      border: `1px solid ${TOKENS.border}`,
                      height: 'auto',
                      py: 0.75,
                      px: 0.5,
                      maxWidth: '100%',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      '& .MuiChip-label': {
                        whiteSpace: 'normal',
                        overflow: 'visible',
                      },
                      '& .MuiChip-icon': { color: TOKENS.secondary, flexShrink: 0 },
                    }}
                  />
                ))}
              </Stack>
            </Box>

            {/* Auth card — constrained so it never escapes the page */}
            <Paper
              elevation={0}
              sx={{
                order: { xs: 1, md: 2 },
                width: '100%',
                maxWidth: { xs: 440, md: '100%' },
                minWidth: 0,
                justifySelf: { xs: 'center', md: 'stretch' },
                mx: { xs: 'auto', md: 0 },
                p: { xs: 2.5, sm: 3.5, md: 4 },
                bgcolor: TOKENS.bgCard,
                border: `1px solid ${TOKENS.borderActive}`,
                borderRadius: 3,
                boxShadow: `0 20px 50px rgba(0, 0, 0, 0.45), 0 0 24px ${TOKENS.accentGlow}`,
                backdropFilter: 'blur(16px)',
                boxSizing: 'border-box',
                overflow: 'hidden',
              }}
            >
              <Stack spacing={2.5} sx={{ minWidth: 0, width: '100%' }}>
                <Box sx={{ minWidth: 0, textAlign: 'center' }}>
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      mx: 'auto',
                      mb: 1.75,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'rgba(30, 58, 138, 0.4)',
                      border: `1.5px solid ${TOKENS.secondary}`,
                      borderRadius: 2,
                    }}
                  >
                    <LogoIcon size={32} />
                  </Box>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 800, mb: 0.5, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                  >
                    {isSignUp ? 'Create Workspace' : 'Welcome Back'}
                  </Typography>
                  <Typography
                    sx={{
                      color: TOKENS.textSecondary,
                      fontSize: '0.875rem',
                      wordBreak: 'break-word',
                    }}
                  >
                    Authorized domain:{' '}
                    <Box component="code" sx={{ color: TOKENS.accent, fontSize: '0.85em' }}>
                      geovera.in
                    </Box>
                  </Typography>
                </Box>

                {infoMessage && (
                  <Alert severity="info" sx={{ wordBreak: 'break-word' }}>
                    {infoMessage}
                  </Alert>
                )}
                {displayError && (
                  <Alert
                    severity="error"
                    onClose={handleClearError}
                    sx={{ wordBreak: 'break-word' }}
                  >
                    {displayError}
                  </Alert>
                )}

                <Box
                  sx={{
                    bgcolor: 'rgba(15, 23, 42, 0.6)',
                    p: { xs: 1.5, sm: 2 },
                    borderRadius: 2,
                    border: `1px solid ${TOKENS.border}`,
                    minWidth: 0,
                  }}
                >
                  <FormControlLabel
                    sx={{
                      alignItems: 'flex-start',
                      m: 0,
                      width: '100%',
                      minWidth: 0,
                      gap: 0.5,
                      '& .MuiFormControlLabel-label': {
                        minWidth: 0,
                        flex: 1,
                      },
                    }}
                    control={
                      <Checkbox
                        checked={agreed}
                        onChange={(e) => setAgreed(e.target.checked)}
                        sx={{
                          color: TOKENS.textSecondary,
                          pt: 0.25,
                          '&.Mui-checked': { color: TOKENS.secondary },
                        }}
                      />
                    }
                    label={
                      <Typography
                        variant="body2"
                        sx={{
                          color: TOKENS.textSecondary,
                          fontSize: '0.8rem',
                          lineHeight: 1.5,
                          wordBreak: 'break-word',
                        }}
                      >
                        I agree to GeoVera&apos;s{' '}
                        <Link
                          component={RouterLink}
                          to="/terms"
                          sx={{ color: TOKENS.textHighlight, fontWeight: 600 }}
                        >
                          Terms of Service
                        </Link>{' '}
                        and{' '}
                        <Link
                          component={RouterLink}
                          to="/privacy"
                          sx={{ color: TOKENS.textHighlight, fontWeight: 600 }}
                        >
                          Privacy Policy
                        </Link>
                        .
                      </Typography>
                    }
                  />
                </Box>

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  startIcon={
                    isLoading ? <CircularProgress size={20} color="inherit" /> : <GoogleIcon />
                  }
                  onClick={handleGoogleAuth}
                  disabled={isLoading || !agreed}
                  sx={{
                    py: 1.5,
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    fontWeight: 700,
                    textTransform: 'none',
                    borderRadius: 2,
                    bgcolor: TOKENS.accent,
                    color: '#FFFFFF',
                    boxShadow: `0 8px 24px ${TOKENS.accentGlow}`,
                    whiteSpace: 'normal',
                    '&:hover': { bgcolor: '#B45309' },
                  }}
                >
                  {isLoading ? 'Signing In...' : 'Continue with Google'}
                </Button>

                <Divider sx={{ borderColor: TOKENS.border }}>
                  <Typography
                    sx={{
                      fontSize: '0.7rem',
                      color: TOKENS.textSecondary,
                      letterSpacing: '0.04em',
                      px: 0.5,
                      textAlign: 'center',
                    }}
                  >
                    AUTOMATIC ACCOUNT PROVISIONING
                  </Typography>
                </Divider>

                <Typography
                  sx={{
                    color: TOKENS.textSecondary,
                    fontSize: '0.8rem',
                    textAlign: 'center',
                    lineHeight: 1.5,
                  }}
                >
                  First time? Google automatically provisions your GeoVera workspace on sign in.
                </Typography>
              </Stack>
            </Paper>
          </Box>
        </Container>
      </Box>

      <Box sx={{ width: '100%', minWidth: 0, mt: 'auto' }}>
        <Footer compact />
      </Box>
    </Box>
  );
};

export default Login;
